import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Users, Search, Download } from "lucide-react";

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleDateString("tr-TR", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

function toCsv(rows) {
  const header = ["İsim", "E-posta", "Şehir", "Kayıt tarihi"];
  const lines = rows.map((u) =>
    [u.name || "", u.email || "", u.city || "", u.created_at || ""]
      .map((v) => `"${String(v).replace(/"/g, '""')}"`)
      .join(",")
  );
  return [header.join(","), ...lines].join("\n");
}

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");

  useEffect(() => {
    api
      .get("/admin/users")
      .then(({ data }) => setUsers(data))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return users;
    return users.filter(
      (u) => u.name?.toLowerCase().includes(q) || u.email?.toLowerCase().includes(q)
    );
  }, [users, query]);

  const exportCsv = () => {
    const csv = toCsv(filtered);
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `kabile-uyeler-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="k-container py-12 md:py-16">
      <div className="flex flex-col items-start justify-between gap-4 md:flex-row md:items-end">
        <div className="flex items-center gap-3">
          <span className="grid h-12 w-12 place-items-center rounded-2xl bg-earth-400/20 text-earth-500">
            <Users className="h-6 w-6" />
          </span>
          <div>
            <h1 className="font-display text-5xl text-ink-900" data-testid="admin-users-title">
              Üyeler
            </h1>
            <p className="font-body text-ink-700">
              {loading ? "Yükleniyor…" : `${users.length} üye`}
            </p>
          </div>
        </div>
        <button
          onClick={exportCsv}
          disabled={loading || filtered.length === 0}
          className="k-btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid="export-users-csv-btn"
        >
          <Download className="mr-2 h-4 w-4" /> CSV olarak dışa aktar
        </button>
      </div>

      <div className="mt-8 relative max-w-md">
        <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-500" />
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="İsim veya e-posta ile ara…"
          className="k-input pl-10"
          data-testid="users-search-input"
        />
      </div>

      <div className="mt-6 overflow-x-auto rounded-2xl border border-cream-300 bg-cream-50">
        <table className="w-full text-left" data-testid="users-table">
          <thead>
            <tr className="border-b border-cream-300 text-xs font-bold uppercase tracking-wide text-ink-500">
              <th className="px-5 py-3">İsim</th>
              <th className="px-5 py-3">E-posta</th>
              <th className="px-5 py-3">Kayıt tarihi</th>
            </tr>
          </thead>
          <tbody>
            {loading &&
              [1, 2, 3].map((i) => (
                <tr key={i} className="border-b border-cream-200">
                  <td colSpan={3} className="px-5 py-4">
                    <div className="h-4 w-full animate-pulse rounded bg-cream-200" />
                  </td>
                </tr>
              ))}
            {!loading && filtered.length === 0 && (
              <tr>
                <td colSpan={3} className="px-5 py-10 text-center font-body text-ink-500" data-testid="users-empty">
                  {query ? "Eşleşen üye bulunamadı." : "Henüz üye yok."}
                </td>
              </tr>
            )}
            {!loading &&
              filtered.map((u) => (
                <tr
                  key={u.user_id}
                  className="border-b border-cream-200 last:border-0 hover:bg-cream-100/60"
                  data-testid={`user-row-${u.user_id}`}
                >
                  <td className="px-5 py-3 font-body font-semibold text-ink-900">{u.name}</td>
                  <td className="px-5 py-3 font-body text-ink-700">
                    <a href={`mailto:${u.email}`} className="text-sage-700 hover:underline">
                      {u.email}
                    </a>
                  </td>
                  <td className="px-5 py-3 font-body text-sm text-ink-500">{formatDate(u.created_at)}</td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
